import { yup } from '@framework/validation/initYup.ts';
import { unionSchema } from '@framework/validation/union/unionSchema.ts';
import { ExpressionType } from '../../IExpression.ts';
import { createExpressionType } from '../../IExpressionType.ts';
import { expressionLogger } from '../../utils/expressionLogger.ts';
import { ReferenceSchema } from '../../validation/ReferenceSchema.ts';
import { AsyncExpressionType } from '../async/AsyncExpression.type.ts';
import { FunctionExpressionType } from '../function/FunctionExpression.type.ts';
import { OperatorsRepository } from '../operators/OperatorsRepository.ts';
import { ReferenceExpressionType } from '../reference/ReferenceExpression.type.ts';
import { SubconditionGroupExpressionSchema } from '../subconditions-group/SubconditionsGroupExpression.type.ts';
import { ValueExpressionType } from '../value/ValueExpression.type.ts';
import { type IConditionArgument } from './IConditionArgument.ts';
import { type IConditionExpression } from './IConditionExpression.ts';

export const ConditionExpressionType =
  createExpressionType<IConditionExpression>({
    type: ExpressionType.Condition,
    schema: yup.object({
      type: yup.string().exact(ExpressionType.Condition).required(),
      reference: ReferenceSchema.label('Riferimento').required(),
      operator: yup.string().label('Operatore').required(),
      arguments: yup
        .array<any, IConditionArgument>(
          unionSchema([
            ValueExpressionType.schema,
            ReferenceExpressionType.schema,
            FunctionExpressionType.schema,
            AsyncExpressionType.schema,
            SubconditionGroupExpressionSchema,
          ]) as yup.Lazy<IConditionArgument>,
        )
        .required(),
    }),
    /**
     * Valuta la condizione, restituendo true o false a seconda del risultato
     * dell'operatore.
     */
    evaluate(expression, scope) {
      // Il campo reference che dobbiamo confrontare
      const referenceValue = scope.getReference(expression.reference);

      // Creiamo uno scope figlio perché dobbiamo valutare le condizioni
      // che potrebbero essere delle SubconditionsGroupExpression
      const argsScope = scope.createChild(expression.reference);

      // I valori con cui confrontare il reference
      const argsValues = expression.arguments.map(argument =>
        argsScope.evaluate(argument),
      );

      const operator = OperatorsRepository.get(expression.operator)!;
      try {
        return operator.compare(
          referenceValue,
          argsValues,
          argsScope,
          expression.reference.field,
        );
      } catch (error: unknown) {
        // biome-ignore format: <tbd>
        expressionLogger.error(`[ConditionExpression.evaluate] Si è verificato un errore nella valutazione dell'operatore ${expression.operator}`, { scope, error, argsValues, expression });
        throw error;
      }
    },
    getReferences(expression, collect) {
      return [expression.reference, ...expression.arguments.flatMap(collect)];
    },
    toString(expression, toString) {
      return `@${expression.reference.code} ${
        expression.operator
      } ${expression.arguments.map(toString).join(', ')}`;
    },
  });
